import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { PropertyMap } from "@/components/PropertyMap";
import { AuthModal } from "@/components/AuthModal";
import { useAuth } from "@/context/AuthContext";
import { Seo, SITE_URL } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { areaLabel, slugify, formatPrice } from "@/lib/format";
import { CATEGORY_PATH, CATEGORY_LABEL } from "@/lib/landing";
import { toast } from "sonner";
import {
  Bed, Bath, Maximize, MapPin, Loader2, Phone, Lock, Crown, ShieldCheck,
  CheckCircle2, MessageCircle, Star, ArrowLeft, Building2,
} from "lucide-react";

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, siteConfig } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [ownerContact, setOwnerContact] = useState(null);
  const [activeImg, setActiveImg] = useState(0);

  const { data: p, isLoading } = useQuery({
    queryKey: ["property", id],
    queryFn: async () => (await api.get(`/properties/${id}`)).data,
  });

  if (isLoading) return <div className="flex justify-center py-40"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>;
  if (!p) return <div className="text-center py-40 text-slate-500">Property not found.</div>;

  const submitEnquiry = async () => {
    if (!form.name || form.phone.replace(/\D/g, "").length !== 10) return toast.error("Enter your name and valid phone");
    setSending(true);
    try {
      await api.post(`/properties/${id}/enquiry`, form);
      toast.success("Enquiry sent to Dotlands Realty. We'll call you soon!");
      setForm({ name: "", phone: "", message: "" });
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  const directMessage = async () => {
    if (!user) return setAuthOpen(true);
    setSending(true);
    try {
      const { data } = await api.post(`/properties/${id}/direct-message`, {
        name: user.name, phone: user.phone, message: form.message || "I'm interested in this property.",
      });
      setOwnerContact(data);
      toast.success("Owner contact unlocked!");
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  const isPremium = user?.is_premium || user?.role === "admin";

  const catLabel = CATEGORY_LABEL[p.property_type] || "Properties";
  const catPath = CATEGORY_PATH[p.property_type] || "/properties";
  const locName = p.locality || p.city;
  const crumbs = [{ name: "Home", to: "/" }, { name: catLabel, to: catPath }];
  if (locName) crumbs.push({ name: locName, to: `/location/${slugify(locName)}` });
  crumbs.push({ name: p.title });

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-6">
      <Seo
        title={`${p.title} | Dotlands Realty`}
        description={(p.description || `${p.title} for sale in ${p.locality ? p.locality + ", " : ""}${p.city}. Price ${p.price_display}. Verified real-estate listing on Dotlands Realty.`).replace(/\s+/g, " ").trim().slice(0, 160)}
        image={p.images?.[0]}
        type="article"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Product",
            name: p.title,
            description: (p.description || p.title),
            image: p.images || [],
            category: p.property_type,
            brand: { "@type": "Organization", name: "Dotlands Realty" },
            offers: {
              "@type": "Offer",
              price: p.total_price || p.price,
              priceCurrency: "INR",
              availability: p.status === "sold" ? "https://schema.org/SoldOut" : "https://schema.org/InStock",
              url: `${SITE_URL}/property/${p.id}`,
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
              { "@type": "ListItem", position: 2, name: "Properties", item: `${SITE_URL}/properties` },
              { "@type": "ListItem", position: 3, name: p.title, item: `${SITE_URL}/property/${p.id}` },
            ],
          },
        ]}
      />
      <Breadcrumbs items={crumbs} />
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Back to results
      </button>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          {/* Gallery */}
          <div className="rounded-2xl overflow-hidden bg-slate-100 aspect-[16/10]">
            <img src={p.images?.[activeImg] || p.images?.[0]} alt={`${p.title} — ${catLabel} in ${locName}`} className="w-full h-full object-cover" data-testid="detail-main-image" />
          </div>
          {p.images?.length > 1 && (
            <div className="flex gap-3 mt-3 overflow-x-auto no-scrollbar">
              {p.images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)}
                  className={`w-24 h-20 rounded-xl overflow-hidden shrink-0 border-2 ${activeImg === i ? "border-emerald-600" : "border-transparent"}`}>
                  <img src={img} alt={`${p.title} photo ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          {/* Title & meta */}
          <div className="mt-6">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-slate-900 text-white">{p.status === "sold" ? "Sold Out" : "For Sale"}</span>
              {p.status === "sold" && <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-red-600 text-white">SOLD OUT</span>}
              {p.featured && <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-[#E7C56A] to-[#A8863A] text-[#0E3B2E] flex items-center gap-1" data-testid="detail-boosted-badge"><Star className="w-3 h-3 fill-[#0E3B2E]" />Boosted</span>}
              <span className="text-xs font-semibold text-emerald-600 uppercase tracking-wide">{p.property_type}</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 mt-3">{p.title}</h1>
            <p className="text-slate-500 flex items-center gap-1.5 mt-2"><MapPin className="w-4 h-4" /> {p.address || `${p.locality}, ${p.city}`}</p>
            {p.survey_number && <p className="text-sm text-slate-500 mt-1">Survey No: <span className="font-semibold text-slate-700">{p.survey_number}</span></p>}
            {(p.extent || p.facing || p.approval) && (
              <p className="text-sm text-slate-500 mt-1 flex flex-wrap gap-x-6 gap-y-1" data-testid="detail-land-fields">
                {p.extent && <span>Extent: <span className="font-semibold text-slate-700">{p.extent}</span></span>}
                {p.facing && <span>Facing: <span className="font-semibold text-slate-700">{p.facing}</span></span>}
                {p.approval && <span>Approval: <span className="font-semibold text-slate-700">{p.approval}</span></span>}
              </p>
            )}
            <div className="text-3xl font-heading font-extrabold text-emerald-700 mt-4" data-testid="detail-price">
              {p.price_note ? p.price_note : (
                <>{p.price_display}{p.price_unit ? <span className="text-base text-slate-400 font-medium">/{areaLabel(p.price_unit)}</span> : null}{p.listing_type === "rent" && <span className="text-base text-slate-400 font-medium">/month</span>}</>
              )}
            </div>
            {p.total_price > 0 && !p.price_note && (
              <div className="text-sm text-slate-500 mt-1" data-testid="detail-total-price">Total value: <span className="font-semibold text-slate-700">{formatPrice(p.total_price)}</span> for {p.area} {areaLabel(p.area_unit)}</div>
            )}

            <div className="flex flex-wrap gap-6 mt-6 py-5 border-y border-slate-200">
              {p.bedrooms > 0 && <Meta icon={Bed} label="Bedrooms" value={p.bedrooms} />}
              {p.bathrooms > 0 && <Meta icon={Bath} label="Bathrooms" value={p.bathrooms} />}
              <Meta icon={Maximize} label="Area" value={`${p.area} ${areaLabel(p.area_unit)}`} />
              <Meta icon={Building2} label="Type" value={p.property_type} />
            </div>

            {p.description && (
              <div className="mt-6">
                <h2 className="font-heading font-bold text-lg text-slate-900 mb-2">Description</h2>
                <p className="text-slate-600 leading-relaxed">{p.description}</p>
              </div>
            )}

            {p.amenities?.length > 0 && (
              <div className="mt-6">
                <h2 className="font-heading font-bold text-lg text-slate-900 mb-3">Amenities</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {p.amenities.map((a) => (
                    <div key={a} className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> {a}</div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6">
              <h2 className="font-heading font-bold text-lg text-slate-900 mb-3">Location</h2>
              <div className="h-72 rounded-2xl overflow-hidden border border-slate-200">
                <PropertyMap properties={[p]} center={[p.lat, p.lng]} zoom={14} />
              </div>
            </div>
          </div>
        </div>

        {/* Contact sidebar */}
        <div className="lg:col-span-1">
          <div className="sticky top-24 space-y-4">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
              <h3 className="font-heading font-bold text-lg text-slate-900">Interested in this property?</h3>
              <p className="text-sm text-slate-500 mt-1">Send an enquiry and our Dotlands desk will call you back.</p>
              <div className="space-y-3 mt-4">
                <div><Label className="mb-1 block text-xs">Name</Label>
                  <Input data-testid="enquiry-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" /></div>
                <div><Label className="mb-1 block text-xs">Phone</Label>
                  <Input data-testid="enquiry-phone" inputMode="numeric" maxLength={10} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "") })} placeholder="10-digit mobile" /></div>
                <div><Label className="mb-1 block text-xs">Message</Label>
                  <Textarea data-testid="enquiry-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="I'm interested..." rows={3} /></div>
                <Button data-testid="enquiry-submit" onClick={submitEnquiry} disabled={sending} className="w-full bg-emerald-600 hover:bg-emerald-700 h-11">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Phone className="w-4 h-4 mr-2" /> Send Enquiry</>}
                </Button>
                {siteConfig?.whatsapp && (
                  <a href={`https://wa.me/${siteConfig.whatsapp}?text=${encodeURIComponent(`Hi Dotlands Realty, I'm interested in "${p.title}" (${p.price_note || p.price_display}) in ${p.locality || p.city}.`)}`}
                    target="_blank" rel="noreferrer" data-testid="whatsapp-dotlands-btn"
                    className="w-full flex items-center justify-center gap-2 border border-emerald-600 text-emerald-700 hover:bg-emerald-50 rounded-lg h-11 text-sm font-semibold transition-colors">
                    <MessageCircle className="w-4 h-4" /> WhatsApp Dotlands
                  </a>
                )}
              </div>
            </div>

            {/* Direct owner contact - gated */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 text-white">
              <div className="flex items-center gap-2 mb-2">
                <Crown className="w-5 h-5 text-amber-400" />
                <h3 className="font-heading font-bold text-lg">Direct Owner Contact</h3>
              </div>
              {ownerContact ? (
                <div className="mt-3 bg-white/10 rounded-xl p-4" data-testid="owner-contact-revealed">
                  <p className="text-sm text-slate-300">Owner: {ownerContact.owner_name || "Verified Owner"}</p>
                  <a href={`tel:${ownerContact.owner_phone}`} className="text-2xl font-heading font-bold text-emerald-400 flex items-center gap-2 mt-1">
                    <Phone className="w-5 h-5" /> +91 {ownerContact.owner_phone}
                  </a>
                  <a href={`https://wa.me/91${ownerContact.owner_phone}`} target="_blank" rel="noreferrer"
                    className="mt-3 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 rounded-lg py-2 text-sm font-semibold">
                    <MessageCircle className="w-4 h-4" /> WhatsApp Owner
                  </a>
                </div>
              ) : isPremium ? (
                <div>
                  <p className="text-sm text-slate-300 mb-3">You're Premium — unlock the verified owner's number & WhatsApp instantly.</p>
                  <Button data-testid="direct-message-btn" onClick={directMessage} disabled={sending} className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reveal Owner Contact"}
                  </Button>
                </div>
              ) : (
                <div>
                  <div className="flex items-center gap-2 bg-white/10 rounded-lg px-3 py-2.5 mb-3">
                    <Lock className="w-4 h-4 text-amber-400" />
                    <span className="text-sm text-slate-300">Owner number hidden — Premium only</span>
                  </div>
                  <p className="text-xs text-slate-400 mb-3">Free members enquire through the Dotlands desk. Upgrade to message owners directly with zero brokerage.</p>
                  <Button data-testid="unlock-premium-btn" onClick={() => user ? navigate("/premium") : setAuthOpen(true)}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold">
                    <Crown className="w-4 h-4 mr-2" /> {user ? "Upgrade to Premium" : "Login to continue"}
                  </Button>
                </div>
              )}
              <div className="flex items-center gap-1.5 mt-3 text-xs text-slate-400">
                <ShieldCheck className="w-3.5 h-3.5" /> All leads verified by Dotlands Realty
              </div>
            </div>
          </div>
        </div>
      </div>
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
    </div>
  );
}

function Meta({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center"><Icon className="w-5 h-5 text-emerald-600" /></div>
      <div><div className="text-xs text-slate-400">{label}</div><div className="font-semibold text-slate-900 capitalize">{value}</div></div>
    </div>
  );
}
